import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Box from 'ui-box';
import { ContactRelationsTable } from '../contacts';
import {
  Button,
  Container,
  defaultColors,
  Grid,
  Icon,
  KeyValue,
  Menu,
  MenuDivider,
  MenuItem,
  minorScale,
  PageContainer,
  Popover,
  Section,
  TabSection,
  Tag,
  useAlert,
  PageLoader,
  Heading,
  majorScale,
  Spinner,
  useToast,
} from '../../npaper';
import { PageHeader } from '../../npaper/PageHeader';
import { SalesTable } from '../sales';
import { CompanyDetailsDialog } from './CompanyDetailsDialog';
import { useStores } from '../../stores';
import { ICompany } from '../../stores/contacts/CompanyStore';
import { PaymentLikelihoodTag } from './PaymentLikelihoodTag';
import { ContactDetailsDialog } from '../contacts/ContactDetailsDialog';
import { SaleDetailDialog } from '../sales/SaleDetailsDialog';
import { AgreementDetailsDialog } from '../agreements/AgreementDetailsDialog';
import { ContactSelect } from '../contacts/ContactSelect';
import { UserThumbnail } from '../settings/UserThumbnail';
import { FormattedTerms } from '../../npaper/FormattedTerms';
import { FormattedMessage, useIntl } from 'react-intl';
import { TimeLine } from '../time-line/TimeLine';
import { getCountryName } from '../../npaper/CountrySelect';
import { ContactGroupsTable } from '../groups/ContactGroupsTable';
import { ContactGroupSelect } from '../groups/ContactGroupSelect';

export const CompanyPage = () => {
  const match = useRouteMatch<{ id: string }>();
  const toaster = useToast();
  const intl = useIntl();
  const id = match.params.id;

  const history = useHistory();

  const [notFound, setNotFound] = useState(false);

  const [company, setCompany] = useState<ICompany>();
  const { companyStore, relationStore, contactGroupStore } = useStores();

  useEffect(() => {
    let isSub = true;
    setCompany(undefined);
    companyStore
      .getItem(id, true)
      .then((comp) => {
        if (isSub) {
          setCompany(comp);
          companyStore.getTimeline(comp);
        }
      })
      .catch((err) => {
        setNotFound(true);
      });
    return () => {
      isSub = false;
    };
  }, [companyStore, id]);

  const [showDetails, setShowDetails] = useState(false);

  const [createContact, setCreateContact] = useState(false);
  const [createSale, setCreateSale] = useState(false);
  const [createAgreement, setCreateAgreement] = useState(false);

  const removeContactAlert = useAlert({
    title: intl.formatMessage({ defaultMessage: 'Remove contact' }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to remove the contact from this company?',
    }),
    intent: 'danger',
    confirmButtonText: intl.formatMessage({ defaultMessage: 'Remove' }),
    icon: 'trash',
  });

  const deleteCompanyAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Deleting company',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'Are you sure you want to delete this company? This action cannot be undone.',
    }),
    intent: 'danger',
    confirmButtonText: intl.formatMessage({
      defaultMessage: 'Delete company',
    }),
    icon: 'trash',
  });

  return !company ? (
    <PageContainer>
      {notFound ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <Icon
            icon="error"
            size={40}
            marginBottom={minorScale(2)}
            color={defaultColors.red[500]}
          />
          <Heading size={400}>
            <FormattedMessage defaultMessage="Company could not be found" />
          </Heading>
          <p className="muted font-mono">ID {id}</p>
        </Box>
      ) : (
        <PageLoader />
      )}
    </PageContainer>
  ) : (
    <PageContainer>
      <CompanyDetailsDialog
        company={company}
        isShown={showDetails}
        onClose={() => setShowDetails(false)}
      />

      <ContactDetailsDialog
        isShown={createContact}
        onClose={(contact) => {
          //contact && history.push(`/contacts/${contact.id}`);
          setCreateContact(false);
        }}
        defaultValue={{ company: company.id }}
      />
      <SaleDetailDialog
        isShown={createSale}
        onClose={(sale) => {
          setCreateSale(false);
          sale && history.push(`/sales/${sale.id}`);
        }}
        defaultValue={{ customer: company.id }}
      />
      <AgreementDetailsDialog
        isShown={createAgreement}
        onClose={(agreement) => {
          setCreateAgreement(false);
          agreement && history.push(`/agreements/${agreement.id}`);
        }}
        defaultValue={{ customer: company.id }}
      />

      <Container>
        <PageHeader
          title={company.name}
          keyName={intl.formatMessage({ defaultMessage: 'Company' })}
          typeIcon="office"
          id={id}
          actions={
            <Popover
              placement="bottom-end"
              content={(close) => (
                <Menu>
                  <MenuDivider
                    text={intl.formatMessage({ defaultMessage: 'Actions' })}
                  />
                  <MenuItem
                    text={intl.formatMessage({
                      defaultMessage: 'Edit details',
                    })}
                    icon="edit"
                    onClick={() => {
                      close();
                      setShowDetails(true);
                    }}
                  />
                  <MenuDivider />
                  <MenuItem
                    icon="trash"
                    text={intl.formatMessage({
                      defaultMessage: 'Delete company',
                    })}
                    intent="danger"
                    onClick={() => {
                      close();
                      deleteCompanyAlert.show(async () => {
                        try {
                          await companyStore.deleteItem(company);
                          history.replace('/companies');
                        } catch (error) {
                          toaster.showDefaultError(error);
                        }
                      });
                    }}
                  />
                </Menu>
              )}
            >
              <Button iconRight="chevron-down">
                <FormattedMessage defaultMessage="Actions" />
              </Button>
            </Popover>
          }
          thumbnail={(company.logo_icon && company.logo_icon.medium) || null}
        />

        <Grid>
          <div className="col-span-4">
            <Section
              title={intl.formatMessage({ defaultMessage: 'Details' })}
              actions={
                <Button
                  iconRight="edit"
                  minimal={true}
                  onClick={() => setShowDetails(true)}
                >
                  <FormattedMessage defaultMessage="Edit" />
                </Button>
              }
            >
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Number' })}
                value={company.company_number}
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'VAT number' })}
                value={
                  company.vat_number ? (
                    <Box
                      className="font-mono"
                      fontWeight={600}
                      display="flex"
                      alignItems="center"
                    >
                      {company.vat_number}
                      {/* TODO: <Icon
                        icon="small-tick"
                        marginLeft="4px"
                        color={defaultColors.green[500]}
                      />*/}
                    </Box>
                  ) : undefined
                }
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Name' })}
                value={company.name}
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Phone' })}
                value={company.phone}
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Email' })}
                value={company.email}
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Domains' })}
                value={company.domains.map((d) => d.domain).join(', ')}
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Country' })}
                value={
                  company.address && company.address.flagEmoji
                    ? `${company.address.flagEmoji} ${getCountryName(
                        intl,
                        company.address.country!
                      )}`
                    : company.address?.country
                }
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Address' })}
                value={company.address && company.address.descriptionLines}
              />
            </Section>

            <Section
              title={intl.formatMessage({
                defaultMessage: 'Payment information',
              })}
              actions={
                <Button
                  iconRight="edit"
                  minimal={true}
                  onClick={() => setShowDetails(true)}
                >
                  <FormattedMessage defaultMessage="Edit" />
                </Button>
              }
            >
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Payment likelihood',
                })}
                value={
                  company.payment_likelihood && (
                    <PaymentLikelihoodTag
                      paymentLikelihood={company.payment_likelihood}
                      showDate
                    />
                  )
                }
              />
              <KeyValue
                keyName={intl.formatMessage({
                  defaultMessage: 'Payment terms',
                })}
                value={
                  <FormattedTerms
                    termsType={company.payment_terms_type}
                    daysOfCredit={company.days_of_credit}
                  />
                }
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Currency' })}
                value={company.currency}
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Default att.' })}
                value={company.default_att}
              />
            </Section>

            <Section
              title={intl.formatMessage({ defaultMessage: 'Administration' })}
              actions={
                <Button
                  iconRight="edit"
                  minimal={true}
                  onClick={() => setShowDetails(true)}
                >
                  <FormattedMessage defaultMessage="Edit" />
                </Button>
              }
            >
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Reference' })}
                value={company.reference}
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Description' })}
                value={company.description}
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Responsible' })}
                value={
                  company.responsible.length > 0 ? (
                    company.responsible.reduce((p, n) => p && !!n, true) ? (
                      company.responsible.map((member, i) => (
                        <UserThumbnail
                          user={member!.user}
                          key={member!.id}
                          showName
                          marginRight={majorScale(2)}
                        />
                      ))
                    ) : (
                      <Spinner size={16} />
                    )
                  ) : undefined
                }
              />
              <KeyValue
                keyName={intl.formatMessage({ defaultMessage: 'Tags' })}
                value={
                  company.tags.length > 0
                    ? company.tags.map((t) => (
                        <Tag
                          key={t}
                          small={true}
                          marginRight={minorScale(1)}
                          intent="info"
                        >
                          {t}
                        </Tag>
                      ))
                    : undefined
                }
              />
            </Section>
          </div>

          <TabSection
            className="col-span-8"
            sections={[
              {
                title: intl.formatMessage({ defaultMessage: 'Sales' }),
                actions: (
                  <Button
                    iconRight="add"
                    minimal={true}
                    onClick={() => setCreateSale(true)}
                  >
                    <FormattedMessage defaultMessage="Create offer" />
                  </Button>
                ),
                content: <SalesTable filter={{ customer: id }} />,
              },
              {
                title: intl.formatMessage({ defaultMessage: 'Contacts' }),
                actions: (
                  <Box display="flex">
                    <ContactSelect
                      placement="bottom-end"
                      onSelect={(contact) => {
                        // TODO: Add contact to company
                        relationStore
                          .createItem({
                            contact: contact.id,
                            company: company.id,
                          })
                          .catch((err) => {
                            if (err.response && err.response.status === 400) {
                              toaster.addToast({
                                message: intl.formatMessage(
                                  {
                                    defaultMessage:
                                      'Contact is already in {companyName}',
                                  },
                                  {
                                    companyName: company.name,
                                  }
                                ),
                                intent: 'warning',
                              });
                            } else {
                              toaster.showDefaultError(err);
                            }
                          });
                      }}
                    >
                      <Button iconRight="link" minimal={true}>
                        <FormattedMessage defaultMessage="Add contact" />
                      </Button>
                    </ContactSelect>
                    <Button
                      iconRight="new-person"
                      minimal={true}
                      onClick={() => setCreateContact(true)}
                    >
                      <FormattedMessage defaultMessage="Create contact" />
                    </Button>
                  </Box>
                ),
                content: (
                  <ContactRelationsTable
                    filter={{ filter: { company: id } }}
                    onRemove={(relation) => {
                      removeContactAlert.show(async () =>
                        // TODO: Remove contact from company
                        relationStore.deleteItem(relation)
                      );
                    }}
                  />
                ),
              } /* TODO: Implement agreements
              {
                title: 'Løbende aftaler',
                actions: (
                  <Button
                    iconRight="add"
                    minimal={true}
                    onClick={() => setCreateAgreement(true)}
                  >
                    Opret aftale
                  </Button>
                ),
                content: <AgreementsTable filter={{ customer: id }} />,
              },*/,
              {
                title: intl.formatMessage({ defaultMessage: 'Groups' }),
                actions: (
                  <>
                    <ContactGroupSelect
                      placement="bottom-end"
                      onSelect={(group) => {
                        contactGroupStore.manageContactsAndCompanies(group, {
                          companies: [company],
                        });
                      }}
                    >
                      <Button iconRight="link" minimal={true}>
                        <FormattedMessage defaultMessage="Add to group" />
                      </Button>
                    </ContactGroupSelect>
                  </>
                ),
                content: (
                  <ContactGroupsTable
                    marginTop={majorScale(-2)}
                    hideHeader
                    filter={{ filter: { companies__in: [id] } }}
                    onRemove={(group) => {
                      contactGroupStore.manageContactsAndCompanies(group, {
                        companies: [company],
                        deleteItems: true,
                      });
                    }}
                  />
                ),
              },
            ]}
          />

          <div className="col-span-12">
            <Section title={intl.formatMessage({ defaultMessage: 'Timeline' })}>
              <TimeLine
                timelineBaseUrl={`${companyStore.apiPath}/${company.id}/timeline/`}
              />
            </Section>
          </div>
        </Grid>
      </Container>
    </PageContainer>
  );
};
